import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Dialog, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import { UserStateContext } from 'context/user-state-context';
const DISMISSED_STORAGE_KEY = 'cinch.betterOnDesktopModalDismissed';
const CINCH_BLUE_600 = '#0875bf';
const CINCH_BLUE_600_HOVER = '#065d99';
const CINCH_BLUE_200 = '#bfe4fc';
const TEXT_PRIMARY = '#0b0c0e';
const TEXT_SECONDARY = 'rgba(1, 12, 20, 0.6)';
const MUI_ELEVATION_2 = '0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)';
export const DesktopWarningModal = () => {
  const {
    user,
    isPLG
  } = useContext(UserStateContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true';
    } catch {
      // Private mode / restrictive extensions can throw on storage access.
      // Fall open (return false) — worst case is one extra reminder.
      return false;
    }
  });
  useEffect(() => {
    // Cross-tab dismissal: if the user clicks "Got It" in another tab, we
    // should stop showing here too.
    const onStorage = e => {
      if (e.key === DISMISSED_STORAGE_KEY && e.newValue === 'true') {
        setDismissed(true);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // The PLG surface is mobile-first, so PLG accounts never see this notice.
  // Contracted accounts keep it — their side includes genuinely desktop-only
  // tools (journey builder, template editors, etc.).
  const shouldShow = !!user && !isPLG && isMobile && !dismissed;
  if (!shouldShow) return null;
  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
    } catch {
      // Fall through — even if the write fails, close the modal so we
      // don't trap the user. They'll just see it again next session.
    }
    setDismissed(true);
  };
  return <Dialog open onClose={handleDismiss} slotProps={{
    paper: {
      sx: {
        width: '326px',
        maxWidth: 'calc(100% - 32px)',
        m: 2,
        p: 3,
        // 24px
        borderRadius: '16px',
        boxSizing: 'border-box'
      }
    }
  }}>
      <Stack spacing={3} alignItems="stretch">
        <Stack spacing={1} alignItems="center">
          <Box sx={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          bgcolor: CINCH_BLUE_200,
          color: CINCH_BLUE_600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
            <DesktopWindowsOutlinedIcon sx={{
            fontSize: 30
          }} />
          </Box>
          <Stack spacing={0.5} alignItems="center" sx={{
          width: '100%'
        }}>
            <Typography component="h2" sx={{
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '28px',
            color: TEXT_PRIMARY,
            m: 0,
            textAlign: 'center'
          }}>
              Better on Desktop
            </Typography>
            <Typography sx={{
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '28px',
            letterSpacing: '0.15px',
            color: TEXT_PRIMARY,
            textAlign: 'center'
          }}>
              Optimize your experience by switching to desktop.
            </Typography>
          </Stack>
        </Stack>

        <Button onClick={handleDismiss} variant="contained" fullWidth disableElevation sx={{
        'bgcolor': CINCH_BLUE_600,
        'color': '#fff',
        'textTransform': 'capitalize',
        'fontWeight': 600,
        'fontSize': '16px',
        'lineHeight': '40px',
        'letterSpacing': '0.46px',
        'borderRadius': '8px',
        'px': 2,
        'py': 0,
        'boxShadow': MUI_ELEVATION_2,
        '&:hover': {
          bgcolor: CINCH_BLUE_600_HOVER,
          boxShadow: MUI_ELEVATION_2
        }
      }}>
          Got It
        </Button>

        <Typography sx={{
        fontSize: '10px',
        fontWeight: 400,
        lineHeight: '12px',
        letterSpacing: '0.1px',
        color: TEXT_SECONDARY,
        textAlign: 'center'
      }}>
          Some features may not display correctly here.
        </Typography>
      </Stack>
    </Dialog>;
};